import React, { useEffect, useRef, useState } from 'react'
import { useSearchParams } from 'react-router-dom';
import { getOrderBookData } from '../../utils/services';
import useWeb3 from '../../hooks/useWeb3';
import { getBackgroundColor, sortOrdersByPriceDifference, convertFromWeiEthers, getTokenSymbol } from '../../utils/helpers';
import { SHARE_DECIMALS } from '../../utils/constants';
import { useSocketEvents } from '../../hooks/dataFetcher/Socket';
import { useTranslation } from 'react-i18next';
import { Accordion } from 'react-bootstrap';
import { useTheme } from '../../contexts/ThemeProvider';

const OrderBook = ({ setOrderBookData, handleOrderFilled, priceoption: priceOption, poolsdetail,tokenToDisplay }) => {
    const { theme } = useTheme();
    const [searchParams] = useSearchParams();
    const { t } = useTranslation();
    const poolId = searchParams.get('id');
    const orderBookLimit = 10
    const [asks, setAsks] = useState([])
    const [bids, setBids] = useState([])
    const [sortedOrderHistory, setSortedOrderHistory] = useState([]);
    const [selectedOption, setSelectedOption] = useState(null);
    const [sumOfAllSharesAsks, setSumOfAllSharesAsks] = useState(0);
    const [sumOfAllSharesBids, setSumOfAllSharesBids] = useState(0);
    const [currentPrice, setCurrentPrice] = useState(null);
    const web3 = useWeb3()
    const SOCKET_EVENT_ORDER_FILLED = `order-filled/${poolId}`
    const SOCKET_EVENT_ORDER_CREATED = `order-created/${poolId}`
    const SOCKET_EVENT_ORDER_CANCELLED = `order-cancelled/${poolId}`
    const BUY_ORDER = 'buy'
    const SELL_ORDER = 'sell'
    const ORDER_FILLED = 'filled'
    const ORDER_PENDING = 'pending'
    const socketQueue = useRef([]);
    // Share quantities use the market token's decimals (USDT 6, USDR 18); a ref keeps socket handlers current
    const shareDecimalsRef = useRef(SHARE_DECIMALS);
    shareDecimalsRef.current = poolsdetail?.token?.tokenDecimals || SHARE_DECIMALS;
    const isProcessing = useRef(false);

    const events = [
        {
            eventName: "connect",
            handler: () => console.log("Connected to the server"),
        },
        {
            eventName: SOCKET_EVENT_ORDER_FILLED,
            handler: (data) => {
                if (data !== null) {
                    const formatedData = data?.filledOrder
                    const convertedData = {
                        ...formatedData,
                        totalShares: convertFromWeiEthers(formatedData?.quantity, shareDecimalsRef.current)
                    }
                    handleOrderFilled(convertedData);
                    socketQueue.current.push({ data: convertedData, type: convertedData?.status });
                    processQueue();
                }
            },
        },
        {
            eventName: SOCKET_EVENT_ORDER_CREATED,
            handler: (data) => {
                if (data !== null) {
                    const formatedData = data?.order
                    const convertedData = {
                        ...formatedData,
                        totalShares: convertFromWeiEthers(formatedData?.quantity, shareDecimalsRef.current)
                    }
                    socketQueue.current.push({ data: convertedData, type: convertedData?.status });
                    processQueue();
                }
            },
        },
        {
            eventName: SOCKET_EVENT_ORDER_CANCELLED,
            handler: (data) => {
                if (data !== null) {
                    const formatedData = data?.order
                    const convertedData = {
                        ...formatedData,
                        totalShares: convertFromWeiEthers(formatedData?.quantity, shareDecimalsRef.current)
                    }
                    socketQueue.current.push({ data: convertedData, type: convertedData?.status });
                    processQueue();
                }
            },
        },
        {
            eventName: "error",
            handler: (error) => console.error("Connection error:", error),
        },
    ];
    useSocketEvents(events);

    const processQueue = () => {
        if (isProcessing.current || socketQueue.current.length === 0) return;

        isProcessing.current = true;
        const { data, type } = socketQueue.current.shift();

        handleSocketData(data, type).finally(() => {
            isProcessing.current = false;
            processQueue();
        });
    };


    const handleSocketData = async (socketSteamData, orderStatus) => {
        return new Promise((resolve) => {
            setSortedOrderHistory(prevSorted => {
                const updatedSorted = prevSorted.map(item => {
                    if (Number(item.option) === Number(socketSteamData?.orderOption)) {
                        let existingAskIndex;
                        if (socketSteamData?.orderType === SELL_ORDER) {
                            existingAskIndex = item.asks.findIndex(
                                ask => Number(ask.pricePerShare) === Number(socketSteamData.pricePerShare)
                            )
                        }
                        else {
                            existingAskIndex = item.bids.findIndex(
                                bid => Number(bid.pricePerShare) === Number(socketSteamData.pricePerShare)
                            )
                        }

                        if (existingAskIndex !== -1) {
                            const updatedAsks = item.asks
                                .map((ask, idx) => {
                                    if (idx === existingAskIndex && socketSteamData?.orderType === SELL_ORDER) {
                                        const updatedTotalShares = orderStatus === ORDER_PENDING
                                            ? Number(ask.totalShares) + Number(socketSteamData.totalShares)
                                            : Number(ask.totalShares) - Number(socketSteamData.totalShares);
                                        return {
                                            ...ask,
                                            totalShares: updatedTotalShares
                                        };
                                    }
                                    return ask;
                                })
                                .filter(ask => Number(ask.totalShares) > 0);

                            const updatedBids = item.bids
                                .map((bid, idx) => {
                                    if (idx === existingAskIndex && socketSteamData?.orderType === BUY_ORDER) {
                                        const updatedTotalShares = orderStatus === ORDER_PENDING
                                            ? Number(bid.totalShares) + Number(socketSteamData.totalShares)
                                            : Number(bid.totalShares) - Number(socketSteamData.totalShares);
                                        return {
                                            ...bid,
                                            totalShares: updatedTotalShares
                                        };
                                    }
                                    return bid;
                                })
                                ?.filter(bid => Number(bid.totalShares) > 0);

                            return {
                                ...item,
                                asks: updatedAsks,
                                bids: updatedBids
                            };
                        } else {
                            if (socketSteamData?.orderType === SELL_ORDER) {
                                return {
                                    ...item,
                                    asks: [
                                        ...item.asks,
                                        {
                                            pricePerShare: socketSteamData.pricePerShare,
                                            totalShares: socketSteamData.totalShares
                                        }
                                    ]?.sort(sortOrdersByPriceDifference(false))

                                }
                            }
                            else {
                                return {
                                    ...item,
                                    bids: [
                                        ...item.bids,
                                        {
                                            pricePerShare: socketSteamData.pricePerShare,
                                            totalShares: socketSteamData.totalShares
                                        }
                                    ]?.sort(sortOrdersByPriceDifference(true))
                                }
                            }
                            // return {
                            //     ...item,
                            //     asks: [
                            //         ...item.asks,
                            //         {
                            //             pricePerShare: socketSteamData.pricePerShare,
                            //             totalShares: socketSteamData.totalShares
                            //         }
                            //     ],
                            //     bids: [
                            //         ...item.bids,
                            //         {
                            //             pricePerShare: socketSteamData.pricePerShare,
                            //             totalShares: socketSteamData.totalShares
                            //         }
                            //     ]
                            // };
                        }
                    }
                    return item;
                });

                const userSelectedAsks = updatedSorted.find(op => op?.optionDetailsId === selectedOption?._id);
                const totalSharesSumAsks = userSelectedAsks?.asks.reduce((sum, ask) => sum + Number(ask.totalShares), 0);
                const totalSharesSumBids = userSelectedAsks?.bids.reduce((sum, bid) => sum + Number(bid.totalShares), 0);
                setAsks(userSelectedAsks?.asks);
                setBids(userSelectedAsks?.bids);
                setOrderBookData(userSelectedAsks?.asks);
                setSumOfAllSharesAsks(totalSharesSumAsks);
                setSumOfAllSharesBids(totalSharesSumBids);

                resolve(updatedSorted);
                return updatedSorted;
            });
        });
    };

    useEffect(() => {
        const firstOptionCurrentPrice = Object?.values(priceOption)[0];
        setCurrentPrice(firstOptionCurrentPrice);
        const getOrderBook = async () => {
            const orderBookData = await getOrderBookData(poolId, orderBookLimit);
            const orderOptions = orderBookData?.map(item => item?.optionDetail);

            const sortedArray = orderOptions.map(opt => {
                const item = orderBookData.find(item => item.optionDetailsId === opt._id);
                if (!item) return null;

                const defaultAsks = (item.asks || [])
                    .map(order => ({
                        ...order,
                        totalShares: convertFromWeiEthers(order?.totalShares, shareDecimalsRef.current),
                    }))
                    ?.filter(order => Number(order.totalShares) > 0)
                    ?.sort(sortOrdersByPriceDifference(false))

                const defaultBids = (item.bids || [])
                    .map(order => ({
                        ...order,
                        totalShares: convertFromWeiEthers(order?.totalShares, shareDecimalsRef.current),
                    }))
                    ?.filter(order => Number(order.totalShares) > 0)
                    ?.sort(sortOrdersByPriceDifference(true))

                return {
                    ...item,
                    asks: defaultAsks.length > 0 ? defaultAsks : [],
                    bids: defaultBids.length > 0 ? defaultBids : [],
                };
            }).filter(Boolean);

            setSortedOrderHistory(sortedArray);
            setSumOfAllSharesAsks(convertFromWeiEthers(sortedArray[0]?.sumOfAllSharesAsks, shareDecimalsRef.current));
            setSumOfAllSharesBids(convertFromWeiEthers(sortedArray[0]?.sumOfAllSharesBids, shareDecimalsRef.current));
            setSelectedOption(sortedArray[0]?.optionDetail);
            setAsks(sortedArray[0]?.asks);
            setBids(sortedArray[0]?.bids);
            setOrderBookData(sortedArray[0]?.asks);
        };

        if (poolId && priceOption) getOrderBook();
    }, [poolId, priceOption]);


    const handleSelectOrderBookOption = (pool) => {
        const selectedOption = sortedOrderHistory?.find(option => option?.optionDetailsId === pool?.optionDetailsId);
        setSumOfAllSharesAsks(convertFromWeiEthers(selectedOption?.sumOfAllSharesAsks, shareDecimalsRef.current))
        setSumOfAllSharesBids(convertFromWeiEthers(selectedOption?.sumOfAllSharesBids, shareDecimalsRef.current))
        setSelectedOption(pool?.optionDetail)
        setAsks(selectedOption?.asks)
        setBids(selectedOption?.bids)
        setOrderBookData(selectedOption?.asks)
        const matchedId = priceOption[pool?.optionDetail?._id];
        setCurrentPrice(matchedId);
    }
        ;

    return (
        <>
            <Accordion className="myposition-box orderlist-accordion">
                <Accordion.Item eventKey="0">
                    <Accordion.Header>{t("Order Book")}</Accordion.Header>
                    <Accordion.Body>
                        <div className="orderlist-section">
                            <div class="dropdown">
                                <button class="dropdown-toggle" type="button" id="dropdownMenuButton1" data-bs-toggle="dropdown" aria-expanded="false">
                                    <div className='leftside'>
                                        <svg xmlns="http://www.w3.org/2000/svg" width="12" height="12" viewBox="0 0 12 12" fill="none">
                                            <circle cx="6" cy="6" r="6" fill={getBackgroundColor(selectedOption?.choiceIndex - 1, theme, sortedOrderHistory?.length) || "#E5FE00"} />
                                        </svg>
                                        {selectedOption?.optionName || "Select Option"}
                                    </div>
                                    <svg className='arrowicon' xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 18 18" fill="none">
                                        <path d="M8.60172 15.5855L3.53922 10.523C3.43367 10.4174 3.37437 10.2743 3.37437 10.125C3.37437 9.97573 3.43367 9.83258 3.53922 9.72703C3.64477 9.62148 3.78792 9.56219 3.93719 9.56219C4.08645 9.56219 4.22961 9.62148 4.33516 9.72703L8.43719 13.8298L8.43719 2.8125C8.43719 2.66332 8.49645 2.52024 8.60194 2.41475C8.70743 2.30926 8.8505 2.25 8.99969 2.25C9.14887 2.25 9.29195 2.30926 9.39743 2.41475C9.50292 2.52024 9.56219 2.66332 9.56219 2.8125L9.56219 13.8298L13.6642 9.72703C13.7698 9.62148 13.9129 9.56219 14.0622 9.56219C14.2115 9.56219 14.3546 9.62148 14.4602 9.72703C14.5657 9.83258 14.625 9.97573 14.625 10.125C14.625 10.2743 14.5657 10.4174 14.4602 10.523L9.39766 15.5855C9.34541 15.6378 9.28338 15.6793 9.21509 15.7076C9.1468 15.7359 9.07361 15.7504 8.99969 15.7504C8.92577 15.7504 8.85257 15.7359 8.78428 15.7076C8.716 15.6793 8.65396 15.6378 8.60172 15.5855Z" fill="#DAD9D9" />
                                    </svg>
                                </button>
                                <ul class="dropdown-menu" aria-labelledby="dropdownMenuButton1">
                                    {sortedOrderHistory?.map((pool, index) => {
                                        return (
                                            <li>
                                                <a onClick={() => handleSelectOrderBookOption(pool)}>
                                                    <svg xmlns="http://www.w3.org/2000/svg" width="12" height="12" viewBox="0 0 12 12" fill="none">
                                                        <circle cx="6" cy="6" r="6" fill={getBackgroundColor(index, theme, sortedOrderHistory?.length)} />
                                                    </svg>
                                                    {pool?.optionDetail?.optionName}
                                                </a>
                                            </li>
                                        )
                                    })}
                                </ul>
                            </div>
                            <div className="sell-order-book">
                                <div className="table-wrapper">
                                    <table>
                                        <thead>
                                            <tr>
                                                <th >{t('Price')}{" "}({tokenToDisplay})</th>
                                                <th>{t('Shares')}</th>
                                                <th>{t('Total')}{" "}({tokenToDisplay})</th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {asks?.slice(0, 7)?.map((order, i) => {
                                                const percent = Math.min(100, (order?.totalShares / sumOfAllSharesAsks) * 100);
                                                return (
                                                    <tr key={`ask-${i}`}>
                                                        <td
                                                            style={{
                                                                background: `linear-gradient(to left, rgba(254, 92, 50, 0.17) ${percent ? percent : 0}%, transparent ${percent ? percent : 0}%)`,
                                                            }}
                                                            colSpan={3}
                                                        >
                                                            <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                                                                <span>{(+order?.pricePerShare)?.toFixed(2)}</span>
                                                                <span style={{ color: "var(--heading-text)" }}>{(+order?.totalShares)?.toFixed(2)}</span>
                                                                <span style={{ color: "var(--heading-text)" }}>{((+order?.pricePerShare) * (+order?.totalShares))?.toFixed(2)}</span>
                                                            </div>
                                                        </td>
                                                    </tr>
                                                );
                                            })}
                                        </tbody>
                                    </table>
                                </div>
                            </div>
                            <p style={{ color: "var(--heading-text)", fontSize: "24px", marginLeft: "20px", display: "flex", alignItems: "center", gap: "6px" }}>{getTokenSymbol(poolsdetail?.token?.tokenSymbol)} {currentPrice ? (+currentPrice)?.toFixed(4) : "0"}</p>
                            <div className="buy-order-book ">
                                <div className="sell-order-book mt-2">
                                    <div className="table-wrapper">
                                        <table>
                                            <tbody>
                                                {bids?.slice(0, 7)?.map((order, i) => {
                                                    const percent = Math.min(100, (order?.totalShares / sumOfAllSharesBids) * 100);
                                                    return (
                                                        <tr key={`ask-${i}`}>
                                                            <td
                                                                style={theme === 'dark' ? {
                                                                    background: `linear-gradient(to left, #283924 ${percent ? percent : 0}%, transparent ${percent ? percent : 0}%)`,
                                                                } : {
                                                                    background: `linear-gradient(to left, #bde5b4 ${percent ? percent : 0}%, transparent ${percent ? percent : 0}%)`,
                                                                }}
                                                                colSpan={3}
                                                            >
                                                                <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                                                                    <span>{(+order?.pricePerShare)?.toFixed(2)}</span>
                                                                    <span style={{ color: "var(--heading-text)" }}>{(+order?.totalShares)?.toFixed(2)}</span>
                                                                    <span style={{ color: "var(--heading-text)" }}>{((+order?.pricePerShare) * (+order?.totalShares))?.toFixed(2)}</span>
                                                                </div>
                                                            </td>
                                                        </tr>
                                                    );
                                                })}
                                            </tbody>
                                        </table>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </Accordion.Body>
                </Accordion.Item>
            </Accordion>
        </>
    )
}

export default OrderBook
